import { FunctionComponent, JSX } from 'react'
import { Box, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material'
import Checkbox from '../../Checkbox/Checkbox.tsx'
import styles from './selectHierarchical.module.scss'
import { HierarchicalOption } from '@/types/Select.ts'

interface SelectHierarchicalProps {
    label?: string
    options: HierarchicalOption[]
    value: string[]
    onChange: (values: string[]) => void
    readOnly?: boolean
    className?: string
    fullWidth?: boolean
    maxToShow?: number
    isParent?: boolean
}

const SelectHierarchical: FunctionComponent<SelectHierarchicalProps> = ({
    label,
    options,
    value,
    onChange,
    readOnly = false,
    className = '',
    fullWidth = false,
    maxToShow,
    isParent = false
}: SelectHierarchicalProps) => {
    function handleToggle(_option: HierarchicalOption, allChildren: string[]): void {
        let newValue: string[] = [...value]
        const allSelected: boolean = allChildren.every((v: string): boolean => newValue.includes(v))
        if (allSelected) newValue = newValue.filter((v: string): boolean => !allChildren.includes(v))
        else newValue = [...new Set([...newValue, ...allChildren])]
        onChange(newValue)
    }

    function handleChildToggle(childValue: string, siblings: string[], parentValue?: string): void {
        let newValue: string[] = [...value]
        if (newValue.includes(childValue)) newValue = newValue.filter((v: string): boolean => v !== childValue)
        else newValue.push(childValue)

        if (parentValue) {
            const selectedSiblings: string[] = siblings.filter((s: string): boolean => newValue.includes(s))
            if (selectedSiblings.length === siblings.length) {
                if (!newValue.includes(parentValue)) newValue.push(parentValue)
            } else if (selectedSiblings.length === 0) {
                newValue = newValue.filter((v: string): boolean => v !== parentValue)
            } else {
                newValue = newValue.filter((v: string): boolean => v !== parentValue)
            }
        }

        onChange(newValue)
    }

    function renderOption(option: HierarchicalOption): JSX.Element {
        if (!option.children || option.children.length === 0) {
            const checked: boolean = value.includes(option.value)
            return (
                <MenuItem
                    key={option.value}
                    value={option.value}
                    onClick={(): void => handleChildToggle(option.value, [])}
                >
                    <Checkbox checked={checked} text={option.name} parent={isParent} />
                </MenuItem>
            )
        }

        const childrenValues: string[] = option.children.map((c: HierarchicalOption): string => c.value)
        const selectedChildren: string[] = childrenValues.filter((v: string): boolean => value.includes(v))
        const allChecked: boolean = selectedChildren.length === childrenValues.length
        const someChecked: boolean = selectedChildren.length > 0 && !allChecked

        return (
            <div key={option.value}>
                <MenuItem
                    value={option.value}
                    onClick={() => handleToggle(option, [option.value, ...childrenValues])}
                    sx={{ fontWeight: 'bold' }}
                >
                    <Checkbox checked={allChecked} indeterminate={someChecked} text={option.name} parent={isParent} />
                </MenuItem>
                {option.children.map(
                    (child: HierarchicalOption): JSX.Element => (
                        <MenuItem
                            key={child.value}
                            value={child.value}
                            sx={{ pl: 4 }}
                            onClick={() => handleChildToggle(child.value, childrenValues, option.value)}
                        >
                            <Checkbox checked={value.includes(child.value)} text={child.name} parent={isParent} />
                        </MenuItem>
                    )
                )}
            </div>
        )
    }

    return (
        <FormControl
            className={`${className} ${fullWidth && styles.FullWidth} ${isParent ? styles.Parent : styles.Daycare}`}
            disabled={readOnly}
        >
            {label && <InputLabel>{label}</InputLabel>}
            <Select
                multiple
                value={value}
                onChange={() => {}}
                input={<OutlinedInput label={label} />}
                renderValue={(): JSX.Element => {
                    const displayed: { value: string; label: string }[] = []

                    function collectVisible(options: HierarchicalOption[]): void {
                        for (const option of options) {
                            if (option.children && option.children.length > 0) {
                                const childrenValues: string[] = option.children.map(
                                    (c: HierarchicalOption): string => c.value
                                )
                                const selectedChildren: string[] = childrenValues.filter((v: string): boolean =>
                                    value.includes(v)
                                )
                                const allChecked: boolean = selectedChildren.length === childrenValues.length
                                const someChecked: boolean = selectedChildren.length > 0 && !allChecked

                                if (allChecked && value.includes(option.value)) {
                                    displayed.push({ value: option.value, label: option.name })
                                } else if (someChecked) {
                                    option.children.forEach((child: HierarchicalOption): void => {
                                        if (value.includes(child.value)) {
                                            displayed.push({ value: child.value, label: child.name })
                                        }
                                    })
                                } else {
                                    collectVisible(option.children)
                                }
                            } else {
                                if (value.includes(option.value)) {
                                    displayed.push({ value: option.value, label: option.name })
                                }
                            }
                        }
                    }

                    collectVisible(options)

                    const visibleValues: { value: string; label: string }[] = displayed.slice(
                        0,
                        maxToShow || displayed.length
                    )
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {visibleValues.map(
                                (v: { value: string; label: string }): JSX.Element => (
                                    <Chip key={v.value} label={v.label} />
                                )
                            )}
                            {maxToShow && displayed.length > maxToShow && (
                                <Chip label={`+${displayed.length - maxToShow}`} />
                            )}
                        </Box>
                    )
                }}
            >
                {options.map(renderOption)}
            </Select>
        </FormControl>
    )
}

export default SelectHierarchical
